import { StateEmitter } from "@onsip/common/libraries/emitter/state-emitter";

import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";

import { Configuration } from "./services/configuration.service";
import {
  BHR,
  BHRHoliday,
  CountryWhitelist,
  CountryBlacklist,
  UrlSubstringWhitelist
} from "@onsip/common/interfaces/organization-configuration";
import {
  WebCallTopic,
  PublicWebCallTopic
} from "@onsip/common/services/api/resources/webCallTopic/web-call-topic";

@Component({
  selector: "onsip-topic-state",
  template: "<div></div>",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export abstract class TopicStateComponent
  extends StateEmitter<PublicWebCallTopic>
  implements OnInit, OnDestroy
{
  targetDisplay!: Observable<Configuration["targetDisplay"]>;
  reps!: Observable<WebCallTopic["webCallTopicReps"] | undefined>;
  video!: Observable<Configuration["video"]>;
  whitelistCountries!: Observable<CountryWhitelist | undefined>;
  blacklistCountries!: Observable<CountryBlacklist | undefined>;
  bhr!: Observable<BHR | undefined>;
  holidays!: Observable<Array<BHRHoliday> | undefined>;
  pages!: Observable<UrlSubstringWhitelist | undefined>;

  protected unsubscriber = new Subscription();

  /** This is what we'll pull from if we decide to blow out changes */
  private readonly _initialState!: PublicWebCallTopic;
  protected get initialState(): PublicWebCallTopic {
    return this._initialState;
  }
  get target(): Configuration["target"] {
    return this._initialState.configuration.target;
  }

  constructor(protected route: ActivatedRoute) {
    super({} as PublicWebCallTopic); // wait for init
  }

  ngOnInit() {
    let didOnce = false;
    this.unsubscriber.add(
      this.route.data.pipe(map(data => data.topic as PublicWebCallTopic)).subscribe(topic => {
        if (!didOnce) (this._initialState as PublicWebCallTopic) = topic;
        didOnce = true;
        this.stateStore = topic;
        this.publishState();
      })
    );

    this.targetDisplay = this.state.pipe(map(state => state.configuration.targetDisplay));
    this.reps = this.state.pipe(map(state => state.webCallTopicReps));
    this.video = this.state.pipe(map(state => state.configuration.video));
    this.whitelistCountries = this.state.pipe(
      map(state => state.filters.countryWhitelist),
      map(unwrapFilter)
    );
    this.blacklistCountries = this.state.pipe(
      map(state => state.filters.countryBlacklist),
      map(unwrapFilter)
    );
    this.bhr = this.state.pipe(
      map(state => state.filters.bhr),
      map(unwrapFilter)
    );
    this.holidays = this.state.pipe(
      map(state => state.filters.bhr),
      map(unwrapFilter),
      map(bhr => (bhr ? bhr.holidays : undefined))
    );
    this.pages = this.state.pipe(
      map(state => state.filters.urlSubstringWhitelist),
      map(unwrapFilter)
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }
}

function unwrapFilter<T>(arg: [T] | undefined): T | undefined {
  return arg ? arg[0] : undefined;
}
